/**
 * lib/examSeating.ts — rooms, desks, and who sits where.
 * ------------------------------------------------------------------
 * Phase 4 of the Exams section, and the last of the four the school scoped on
 * 2026-09-18. Pure, like examDuties and examRegister beside it.
 *
 * A SEATING PLAN IS PER SESSION, NOT PER PAPER. 25 of the live series' 48
 * sessions run more than one paper at the same time in the same room — that
 * is how the school staffs a session, and it is why a duty counts once. So a
 * desk belongs to a CANDIDATE in a session, and the plan records which papers
 * that candidate is sitting at it. The biggest session seats 81 candidates
 * across three papers; thirteen seat ten or fewer.
 *
 * DESKS ARE A GRID, because "third row, second from the left" is how a person
 * finds a candidate in a hall, and a capacity number cannot say it. A room is
 * rows × columns with any dead desks marked, and only usable desks are
 * numbered — numbering the dead ones leaves gaps that read as a mistake.
 *
 * CANDIDATE-NUMBER ORDER, filling room by room (the school, 2026-09-18): the
 * convention centres use, and the one that makes a candidate findable. It
 * also spreads candidates sitting one paper through the room rather than
 * seating them in a block.
 *
 * RE-ALLOCATING KEEPS THE SEATS PEOPLE ALREADY HAVE. A plan is printed and
 * stuck on desks days early; a candidate added on the Friday must not move
 * everybody else. `allocateSession` is given what exists and only fills the
 * gaps, unless it is told to start again.
 */

export interface ExamRoom {
  id: string;
  name: string;
  rows: number;
  cols: number;
  /** 'r2c3' — a desk that cannot be used (a pillar, a broken chair, a gap
   *  left deliberately for spacing). */
  unusable?: string[];
  active?: boolean;
  notes?: string;
  /** Which wall the board is on, and where the doors are — the room as it is
   *  actually arranged, drawn from above (lib/examRoomLayout). */
  board?: 'front' | 'back' | 'left' | 'right';
  doors?: { wall: 'front' | 'back' | 'left' | 'right'; at: number }[];
  /** Where the invigilator's desk is. Absent: beside the board. */
  invigilator?: { wall: 'front' | 'back' | 'left' | 'right'; at: number };
}

export interface Desk {
  /** 1-based, in reading order, counting only usable desks. */
  number: number;
  row: number;
  col: number;
  key: string;
}

export interface Seat {
  roomId: string;
  roomName: string;
  desk: number;
  row: number;
  col: number;
}

/** One candidate needing one desk in one session. */
export interface SeatingCandidate {
  studentId: string;
  candidateNumber: string;
  candidateName: string;
  className: string;
  /** Every paper they sit in this session — usually one, sometimes two. */
  codes: string[];
}

export const deskKey = (row: number, col: number): string => `r${row}c${col}`;

/** Every usable desk, in reading order: row 1 left to right, then row 2. */
export function desksOf(room: ExamRoom): Desk[] {
  const dead = new Set(room.unusable ?? []);
  const out: Desk[] = [];
  let n = 0;
  for (let row = 1; row <= Math.max(0, room.rows); row += 1) {
    for (let col = 1; col <= Math.max(0, room.cols); col += 1) {
      const key = deskKey(row, col);
      if (dead.has(key)) continue;
      n += 1;
      out.push({ number: n, row, col, key });
    }
  }
  return out;
}

export function roomCapacity(room: ExamRoom): number {
  return desksOf(room).length;
}

export function totalCapacity(rooms: ExamRoom[]): number {
  return rooms.reduce((n, room) => n + roomCapacity(room), 0);
}

/* ── who needs a desk ─────────────────────────────────────────────── */

interface EntryLike { code: string; date: string; session: string }
interface CandidateLike {
  studentId: string;
  candidateNumber: string;
  candidateName: string;
  className?: string;
  components?: EntryLike[];
}

/**
 * Everyone sitting anything in one session, ONCE, with the papers they sit.
 *
 * A candidate on two concurrent papers needs one desk, not two — the same
 * fact that makes a duty a session rather than a paper. A test-window paper
 * carries no session on the statement, so an empty session matches whatever
 * the school scheduled it into (`entryMatchesPaper` does the same).
 */
export function sessionCandidates(
  candidates: CandidateLike[],
  date: string,
  session: string,
): SeatingCandidate[] {
  const out: SeatingCandidate[] = [];
  for (const c of candidates) {
    const codes = (c.components ?? [])
      .filter((e) => e.date === date && (!e.session || !session || e.session === session))
      .map((e) => e.code);
    if (!codes.length) continue;
    out.push({
      studentId: c.studentId,
      candidateNumber: c.candidateNumber,
      candidateName: c.candidateName,
      className: c.className ?? '',
      codes: [...new Set(codes)].sort(),
    });
  }
  return out.sort((a, b) => a.candidateNumber.localeCompare(b.candidateNumber, undefined, { numeric: true })
    || a.candidateName.localeCompare(b.candidateName));
}

/* ── the allocation ───────────────────────────────────────────────── */

export interface Allocation {
  seats: Record<string, Seat>;
  /** Candidates with no desk — the rooms chosen do not hold everybody. */
  unseated: SeatingCandidate[];
  /** Seats held by candidates who are no longer entered for the session. */
  stale: string[];
}

/**
 * Seat a session.
 *
 * @param existing seats already given out, KEPT unless `fresh` is set: a plan
 *                 is printed and taped to desks days in advance, so a
 *                 candidate added on the Friday fills a gap rather than
 *                 moving eighty people.
 */
export function allocateSession(
  candidates: SeatingCandidate[],
  rooms: ExamRoom[],
  existing: Record<string, Seat> = {},
  fresh = false,
): Allocation {
  const usable = rooms.filter((r) => r.active !== false);
  const roomById = new Map(usable.map((r) => [r.id, r]));
  const wanted = new Set(candidates.map((c) => c.studentId));

  const seats: Record<string, Seat> = {};
  const taken = new Set<string>();

  if (!fresh) {
    for (const [studentId, seat] of Object.entries(existing)) {
      if (!wanted.has(studentId)) continue;
      const room = roomById.get(seat.roomId);
      // A seat in a room no longer in use, or on a desk that has since been
      // marked dead, is not a seat: that candidate is placed again.
      if (!room || !desksOf(room).some((d) => d.number === seat.desk)) continue;
      const key = `${seat.roomId}|${seat.desk}`;
      if (taken.has(key)) continue;
      seats[studentId] = seat;
      taken.add(key);
    }
  }

  const free: Seat[] = [];
  for (const room of usable) {
    for (const desk of desksOf(room)) {
      const key = `${room.id}|${desk.number}`;
      if (taken.has(key)) continue;
      free.push({ roomId: room.id, roomName: room.name, desk: desk.number, row: desk.row, col: desk.col });
    }
  }

  const unseated: SeatingCandidate[] = [];
  let next = 0;
  for (const c of candidates) {
    if (seats[c.studentId]) continue;
    if (next >= free.length) { unseated.push(c); continue; }
    seats[c.studentId] = free[next];
    next += 1;
  }

  const stale = Object.keys(existing).filter((studentId) => !wanted.has(studentId));
  return { seats, unseated, stale };
}

/** Move one candidate to a desk, swapping with whoever is already on it. */
export function moveSeat(
  seats: Record<string, Seat>,
  studentId: string,
  to: Seat,
): Record<string, Seat> {
  const next = { ...seats };
  const from = next[studentId];
  const sittingThere = Object.entries(next)
    .find(([id, s]) => id !== studentId && s.roomId === to.roomId && s.desk === to.desk);
  next[studentId] = to;
  if (sittingThere) {
    const [otherId] = sittingThere;
    // A swap, not an eviction: the other candidate takes the vacated desk, or
    // loses their seat only if the mover had none.
    if (from) next[otherId] = from; else delete next[otherId];
  }
  return next;
}

/* ── reading the plan ─────────────────────────────────────────────── */

export interface PlannedDesk extends Desk {
  candidate: SeatingCandidate | null;
}

/** One room's desks with their occupants, laid out as rows for drawing. */
export function roomPlan(
  room: ExamRoom,
  candidates: SeatingCandidate[],
  seats: Record<string, Seat>,
): PlannedDesk[][] {
  const byDesk = new Map<number, SeatingCandidate>();
  for (const c of candidates) {
    const seat = seats[c.studentId];
    if (seat && seat.roomId === room.id) byDesk.set(seat.desk, c);
  }
  const rows: PlannedDesk[][] = [];
  for (const desk of desksOf(room)) {
    const row = rows[desk.row - 1] ?? [];
    row.push({ ...desk, candidate: byDesk.get(desk.number) ?? null });
    rows[desk.row - 1] = row;
  }
  return rows.filter(Boolean);
}

/** The rooms actually used by an allocation, in the order they were filled. */
export function roomsUsed(rooms: ExamRoom[], seats: Record<string, Seat>): ExamRoom[] {
  const used = new Set(Object.values(seats).map((s) => s.roomId));
  return rooms.filter((r) => used.has(r.id));
}

export interface SeatingProblem {
  kind: 'overCapacity' | 'unseated' | 'doubleBooked' | 'noRooms';
  detail: string;
}

/** What is wrong with a plan, in the words the office would use. */
export function seatingProblems(
  candidates: SeatingCandidate[],
  rooms: ExamRoom[],
  seats: Record<string, Seat>,
): SeatingProblem[] {
  const out: SeatingProblem[] = [];
  if (!rooms.length) {
    out.push({ kind: 'noRooms', detail: 'No room has been chosen for this session.' });
    return out;
  }
  const capacity = totalCapacity(rooms);
  if (capacity < candidates.length) {
    out.push({
      kind: 'overCapacity',
      detail: `${candidates.length} candidates and ${capacity} desks — ${candidates.length - capacity} short.`,
    });
  }
  const unseated = candidates.filter((c) => !seats[c.studentId]);
  if (unseated.length) {
    out.push({
      kind: 'unseated',
      detail: `${unseated.length} candidate${unseated.length === 1 ? ' has' : 's have'} no desk: `
        + `${unseated.slice(0, 4).map((c) => c.candidateNumber).join(', ')}${unseated.length > 4 ? '…' : ''}.`,
    });
  }
  const seen = new Map<string, string>();
  for (const c of candidates) {
    const seat = seats[c.studentId];
    if (!seat) continue;
    const key = `${seat.roomId}|${seat.desk}`;
    const other = seen.get(key);
    if (other) {
      out.push({
        kind: 'doubleBooked',
        detail: `${seat.roomName} desk ${seat.desk} has two candidates: ${other} and ${c.candidateNumber}.`,
      });
    } else seen.set(key, c.candidateNumber);
  }
  return out;
}

/** `{seriesId}__{date}__{session}` — one plan per session. */
export function seatingId(seriesId: string, date: string, session: string): string {
  return `${seriesId}__${date}__${session}`;
}

/* ── how a room is filled ─────────────────────────────────────────── */

/**
 * The two orders the school seats a hall in (2026-09-22). Both are
 * BOUSTROPHEDON — they turn at the end and come back — because that is how
 * invigilators walk a room, and it keeps consecutive candidate numbers apart
 * at the turn rather than side by side.
 *
 *   rows      row 1 left to right, row 2 right to left, row 3 left to right…
 *   columns   column 1 front to back, column 2 back to front, column 3 front…
 *
 * `reading` is the original order (every row left to right) and stays the
 * default for "Seat the candidates", so a plan made before this still fills
 * the same way.
 */
export type FillPattern = 'reading' | 'rows' | 'columns';

export const FILL_PATTERN_LABEL: Record<FillPattern, string> = {
  reading: 'Left to right, row by row',
  rows: 'Snake along the rows (→ then ←)',
  columns: 'Snake down the columns (↓ then ↑)',
};

/** Every usable desk of a room, in the order this pattern fills them. */
export function deskOrder(room: ExamRoom, pattern: FillPattern): Desk[] {
  const all = desksOf(room);
  if (pattern === 'reading') return all;
  const at = new Map(all.map((d) => [d.key, d]));
  const out: Desk[] = [];
  if (pattern === 'rows') {
    for (let row = 1; row <= room.rows; row += 1) {
      const cols = Array.from({ length: room.cols }, (_, i) => i + 1);
      // Even rows come back the other way — the snake's turn.
      if (row % 2 === 0) cols.reverse();
      for (const col of cols) { const d = at.get(deskKey(row, col)); if (d) out.push(d); }
    }
    return out;
  }
  for (let col = 1; col <= room.cols; col += 1) {
    const rows = Array.from({ length: room.rows }, (_, i) => i + 1);
    if (col % 2 === 0) rows.reverse();
    for (const row of rows) { const d = at.get(deskKey(row, col)); if (d) out.push(d); }
  }
  return out;
}

/** The desks of several rooms, one room filled before the next is started. */
export function fillOrder(rooms: ExamRoom[], pattern: FillPattern): Seat[] {
  return rooms.flatMap((room) => deskOrder(room, pattern).map((d) => ({
    roomId: room.id, roomName: room.name, desk: d.number, row: d.row, col: d.col,
  })));
}

export interface PatternGuess {
  pattern: FillPattern;
  /** How many placements agree with it — what the offer says out loud. */
  placed: number;
}

/**
 * Watch someone seat a room by hand and work out which order they are using.
 *
 * `placedInOrder` is the desks they filled, in the order the candidates
 * appear on their list. The guess is offered only once the hand cannot be
 * read two ways: three desks in, AND the sequence has TURNED — reached the
 * end of a row or column and started the next. Until the turn, "left to
 * right along the front row" is the opening of both patterns and of plain
 * reading order, and guessing then would finish the room the wrong way round.
 */
export function detectFillPattern(room: ExamRoom, placedInOrder: number[]): PatternGuess | null {
  const placed = placedInOrder.filter((n, i, a) => a.indexOf(n) === i);
  if (placed.length < 3) return null;

  const matches = (pattern: FillPattern) => {
    const order = deskOrder(room, pattern).map((d) => d.number);
    return placed.every((n, i) => order[i] === n);
  };
  const fits = (['rows', 'columns', 'reading'] as FillPattern[]).filter(matches);
  if (!fits.length) return null;
  // Still inside the first run: every pattern that fits agrees so far, so
  // there is nothing to choose between them yet.
  const first = deskOrder(room, fits[0]);
  const turned = placed.length > runLength(room, fits[0]);
  if (!turned) return null;
  // Two patterns can only still agree past the turn in a single-row or
  // single-column room, where they are the same order anyway.
  void first;
  return { pattern: fits[0], placed: placed.length };
}

/** How many desks the first row (or column) of this pattern holds. */
function runLength(room: ExamRoom, pattern: FillPattern): number {
  const dead = new Set(room.unusable ?? []);
  let n = 0;
  if (pattern === 'columns') {
    for (let row = 1; row <= room.rows; row += 1) if (!dead.has(deskKey(row, 1))) n += 1;
  } else {
    for (let col = 1; col <= room.cols; col += 1) if (!dead.has(deskKey(1, col))) n += 1;
  }
  return n;
}

/**
 * Finish what the hand started: seat everyone still standing, in candidate
 * order, on the free desks the pattern reaches next.
 *
 * Only ADDS. Nobody already on a desk is moved, which is the same promise
 * `allocateSession` makes and for the same reason — the plan may be printed.
 */
export function completePattern(
  candidates: SeatingCandidate[],
  rooms: ExamRoom[],
  seats: Record<string, Seat>,
  pattern: FillPattern,
): Allocation {
  const usable = rooms.filter((r) => r.active !== false);
  const taken = new Set(Object.values(seats).map((s) => `${s.roomId}|${s.desk}`));
  const free = fillOrder(usable, pattern).filter((s) => !taken.has(`${s.roomId}|${s.desk}`));
  const next = { ...seats };
  const unseated: SeatingCandidate[] = [];
  let i = 0;
  for (const c of candidates) {
    if (next[c.studentId]) continue;
    if (i >= free.length) { unseated.push(c); continue; }
    next[c.studentId] = free[i];
    i += 1;
  }
  const wanted = new Set(candidates.map((c) => c.studentId));
  return { seats: next, unseated, stale: Object.keys(seats).filter((id) => !wanted.has(id)) };
}

/* ── the automatic plan, and what the office can tell it ──────────── */

/**
 * How close candidates sit (the school, 2026-10-15). The office leaves a
 * table between candidates when a small paper is in a big room, and the
 * automatic plan used to pack them shoulder to shoulder at the front.
 *
 *   every     every desk
 *   besides   a desk left empty between candidates along a row
 *   rows      every other row left empty
 *   chess     a chessboard: nobody beside, in front or behind
 *   spread    the most space the room allows: the first of chess, besides,
 *             rows, every that still seats everybody standing
 *
 * `spread` is the default, so a small paper in the Hall is spaced out without
 * anybody asking, and a full one falls back to every desk.
 */
export type Spacing = 'spread' | 'every' | 'besides' | 'rows' | 'chess';

export const SPACING_LABEL: Record<Spacing, string> = {
  spread: 'As spread out as the rooms allow',
  every: 'Every desk',
  besides: 'Skip a desk along each row',
  rows: 'Skip every other row',
  chess: 'Chessboard (nobody beside, in front or behind)',
};

/** Whether a spacing lets a candidate sit at this desk (row and column from 1). */
export function deskAllowed(spacing: Exclude<Spacing, 'spread'>, row: number, col: number): boolean {
  if (spacing === 'besides') return col % 2 === 1;
  if (spacing === 'rows') return row % 2 === 1;
  if (spacing === 'chess') return (row + col) % 2 === 0;
  return true;
}

/**
 * The order candidates are handed desks in.
 *
 *   number     candidate-number order, papers mixed (the original)
 *   paper      one paper's candidates together, then the next paper's
 *   alternate  the papers taken in turn, so neighbours sit different papers
 */
export type SeatOrder = 'number' | 'paper' | 'alternate';

export const SEAT_ORDER_LABEL: Record<SeatOrder, string> = {
  number: 'Candidate-number order',
  paper: 'Paper by paper, each paper together',
  alternate: 'Papers in turn, so neighbours sit different papers',
};

const byNumber = (a: SeatingCandidate, b: SeatingCandidate) =>
  a.candidateNumber.localeCompare(b.candidateNumber, undefined, { numeric: true })
  || a.candidateName.localeCompare(b.candidateName);

/** The candidates in the order the chosen rule seats them. */
export function orderCandidates(candidates: SeatingCandidate[], order: SeatOrder, paperOrder: string[] = []): SeatingCandidate[] {
  const sorted = [...candidates].sort(byNumber);
  if (order === 'number') return sorted;
  const groups = new Map<string, SeatingCandidate[]>();
  for (const c of sorted) {
    const key = c.codes.join('+');
    groups.set(key, [...(groups.get(key) ?? []), c]);
  }
  // The office's order of the exams where it gave one, by code otherwise.
  const rank = (k: string) => { const i = paperOrder.indexOf(k.split('+')[0]); return i < 0 ? paperOrder.length : i; };
  const keys = [...groups.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  if (order === 'paper') return keys.flatMap((k) => groups.get(k)!);
  // In turn, the biggest paper first: it is the one most likely to end in a
  // run once the smaller ones are used up, and the run is then at the back.
  const queues = keys.map((k) => [...groups.get(k)!]).sort((a, b) => b.length - a.length);
  const out: SeatingCandidate[] = [];
  while (queues.some((q) => q.length)) {
    for (const q of queues) { const c = q.shift(); if (c) out.push(c); }
  }
  return out;
}

export interface AutoOptions {
  pattern: FillPattern;
  spacing: Spacing;
  order: SeatOrder;
  /** Desks left empty in THIS session only, `${roomId}|r2c3` — a room's dead
   *  desks are the room's business, these are the plan's. */
  blocked?: string[];
  /** At most this many candidates in a room (by room id). */
  limits?: Record<string, number>;
  /** The order the exams are seated in, by code, for `order: 'paper'`. */
  paperOrder?: string[];
  /** Start each exam at the top of a fresh column (or row), as the office
   *  does by hand, where the rooms have the desks to spare. */
  freshLinePerPaper?: boolean;
}

/** Which column (or row) of which room a desk is in: one run of the fill. */
const runOf = (seat: Seat, pattern: FillPattern) =>
  `${seat.roomId}|${pattern === 'columns' ? `c${seat.col}` : `r${seat.row}`}`;

/**
 * Hand out the free desks in order.
 *
 * With `fresh` (the office's hand, 16 October 2026): each exam starts at the
 * FRONT of a fresh column (or row), and the snake starts again from there,
 * but only while the desks left over can still hold everybody still
 * standing. Where they cannot, the next exam carries straight on, as the
 * office let the last small exam follow on in the last column.
 */
function placeInOrder(standing: SeatingCandidate[], free: Seat[], pattern: FillPattern, fresh: boolean): Record<string, Seat> {
  const out: Record<string, Seat> = {};
  if (!fresh) {
    standing.forEach((c, i) => { if (i < free.length) out[c.studentId] = free[i]; });
    return out;
  }
  // The runs (columns or rows) in fill order, each front to back (or left to right).
  const runs: Seat[][] = [];
  const byRun = new Map<string, Seat[]>();
  for (const seat of free) {
    const k = runOf(seat, pattern);
    if (!byRun.has(k)) { byRun.set(k, []); runs.push(byRun.get(k)!); }
    byRun.get(k)!.push(seat);
  }
  for (const run of runs) run.sort((a, b) => (pattern === 'columns' ? a.row - b.row : a.col - b.col));
  const snake = pattern !== 'reading';
  let r = -1; let forward = true; let queue: Seat[] = [];
  const open = (next: number, dir: boolean) => { r = next; forward = dir; queue = dir ? [...runs[r]] : [...runs[r]].reverse(); };
  const after = (from: number) => runs.slice(from + 1).reduce((n, run) => n + run.length, 0);
  let lastKey = '';
  standing.forEach((c, i) => {
    const key = c.codes.join('+');
    const left = standing.length - i;
    if (r < 0) open(0, true);
    else if (key !== lastKey && queue.length && r + 1 < runs.length && after(r) >= left) open(r + 1, true);
    while (!queue.length && r + 1 < runs.length) open(r + 1, key !== lastKey ? true : (snake ? !forward : true));
    const seat = queue.shift();
    if (seat) out[c.studentId] = seat;
    lastKey = key;
  });
  return out;
}

export const blockKey = (roomId: string, row: number, col: number): string => `${roomId}|${deskKey(row, col)}`;

/** The desks still free under these options, in the order they are filled. */
export function freeDesks(
  rooms: ExamRoom[],
  seats: Record<string, Seat>,
  options: AutoOptions,
  spacing: Exclude<Spacing, 'spread'>,
): Seat[] {
  const blocked = new Set(options.blocked ?? []);
  const taken = new Set(Object.values(seats).map((s) => `${s.roomId}|${s.desk}`));
  const out: Seat[] = [];
  for (const room of rooms.filter((r) => r.active !== false)) {
    const limit = options.limits?.[room.id];
    let inRoom = Object.values(seats).filter((s) => s.roomId === room.id).length;
    for (const d of deskOrder(room, options.pattern)) {
      if (limit !== undefined && inRoom >= limit) break;
      if (taken.has(`${room.id}|${d.number}`) || blocked.has(blockKey(room.id, d.row, d.col))) continue;
      if (!deskAllowed(spacing, d.row, d.col)) continue;
      out.push({ roomId: room.id, roomName: room.name, desk: d.number, row: d.row, col: d.col });
      inRoom += 1;
    }
  }
  return out;
}

/** `spread` made concrete: the widest spacing that still seats `needed`. */
export function resolveSpacing(
  rooms: ExamRoom[],
  seats: Record<string, Seat>,
  options: AutoOptions,
  needed: number,
): Exclude<Spacing, 'spread'> {
  if (options.spacing !== 'spread') return options.spacing;
  for (const s of ['chess', 'besides', 'rows'] as const) {
    if (freeDesks(rooms, seats, options, s).length >= needed) return s;
  }
  return 'every';
}

/**
 * The automatic plan. Like `completePattern` it only ADDS — anyone already on
 * a desk stays there, because the plan may be printed — so "re-seat
 * everybody" is this with no seats passed in.
 */
export function autoSeat(
  candidates: SeatingCandidate[],
  rooms: ExamRoom[],
  seats: Record<string, Seat>,
  options: AutoOptions,
): Allocation & { spacing: Exclude<Spacing, 'spread'> } {
  const wanted = new Set(candidates.map((c) => c.studentId));
  const kept: Record<string, Seat> = {};
  for (const [id, s] of Object.entries(seats)) if (wanted.has(id)) kept[id] = s;
  const standing = orderCandidates(candidates.filter((c) => !kept[c.studentId]), options.order, options.paperOrder);
  const spacing = resolveSpacing(rooms, kept, options, standing.length);
  const free = freeDesks(rooms, kept, options, spacing);
  // A fresh column per exam only while there are desks to spare for it.
  const placed = placeInOrder(standing, free, options.pattern, !!options.freshLinePerPaper && options.order === 'paper');
  const next = { ...kept, ...placed };
  const unseated = standing.filter((c) => !placed[c.studentId]);
  return { seats: next, unseated, stale: Object.keys(seats).filter((id) => !wanted.has(id)), spacing };
}

/** Send a candidate back to the list. */
export function unseat(seats: Record<string, Seat>, studentId: string): Record<string, Seat> {
  const next = { ...seats };
  delete next[studentId];
  return next;
}
